import { authenticatedFetch } from "@utils/authenticatedFetch";
import { getApiOrigin } from "@utils/apiOrigin";

const BASE_URL = getApiOrigin();
const UPLOAD_ENDPOINT = "/api/v1/mock-survey/mock-survey-file-upload";
const LIST_ENDPOINT = "/api/v1/mock-survey/mock-survey-file-get";
const REMOVE_ENDPOINT = "/api/v1/mock-survey/mock-survey-file-remove";
const ADD_COMMENT_ENDPOINT = "/api/v1/mock-survey/mock-survey-file-add-comment";
const GET_COMMENTS_ENDPOINT = "/api/v1/mock-survey/mock-survey-file-comments";
const REMOVE_COMMENT_ENDPOINT = "/api/v1/mock-survey/mock-survey-file-remove-comment";
const UPDATE_COMMENT_ENDPOINT = "/api/v1/mock-survey/mock-survey-file-comments/update";

export interface MockSurveyUploadResponse {
	success: boolean;
	message: string;
	file_id?: string | number;
	filename?: string;
	facility?: string;
	created_at?: string;
	[extra: string]: any;
}

export interface RemoveCommentRequest {
	author: string;
	commentId: number;
	commentText: string;
	endOffset: number;
	file_id: number;
	paragraphIndex: number;
	startOffset: number;
}

export interface RemoveCommentResponse {
	success: boolean;
	message?: string;
	deleted?: boolean;
	comment_id?: number;
}

export interface UpdateCommentRequest {
	id: number;
	fileid: number;
	commentText: string;
	author?: string;
	initials?: string;
	fileName?: string;
	paragraphIndex?: number;
	startOffset?: number;
	endOffset?: number;
	selectedText?: string;
	docCommentId?: string | number | null; // DOCX comment ID for embedded comments
}

export interface UpdateCommentResponse {
	status: string;
	message: string;
	id: number;
	fileid: number;
	commentText: string;
}

export const mockSurveyService = {
	async uploadFile(
		file: File,
		facility: string,
		options?: {
			documentId?: number | string;
			folderId?: number | string;
			fileId?: number | string; // AffableBPM ImageId
		}
	): Promise<MockSurveyUploadResponse> {
		const formData = new FormData();
		formData.append("file", file);
		formData.append("facility", facility || "test");

		// Add AffableBPM IDs if provided
		if (options?.documentId !== undefined) {
			formData.append("DocumentId", String(options.documentId));
		}
		if (options?.folderId !== undefined) {
			formData.append("FolderId", String(options.folderId));
		}
		if (options?.fileId !== undefined) {
			formData.append("FileId", String(options.fileId));
		}

		const url = `${BASE_URL}${UPLOAD_ENDPOINT}`;
		console.log("📤 Uploading to:", url, {
			filename: file.name,
			facility,
			fileSize: file.size,
			documentId: options?.documentId,
			folderId: options?.folderId,
			fileId: options?.fileId,
		});

		const response = await authenticatedFetch(url,
			{
				method: "POST",
				body: formData,
			}
		);

		console.log("📡 Response status:", response.status, response.statusText);

		if (!response.ok) {
			const text = await response.text().catch(() => "");
			console.error("❌ Upload failed:", response.status, text);
			throw new Error(`Mock survey upload failed: ${response.status} ${response.statusText} ${text}`);
		}

		const result = await response.json() as MockSurveyUploadResponse;
		console.log("✅ Upload successful:", result);
		return result;
	},

	async listFiles(facility: string, isAffable?: boolean): Promise<{ count: number; items: Array<{ id: number | string; facility: string; userId?: number; filename: string; filecontent?: string; created_at?: string }> }> {
		const params = new URLSearchParams();
		if (facility) params.set("facility", facility);
		if (isAffable === true) {
			params.set("IsAffable", "True");
		}

		const url = `${BASE_URL}${LIST_ENDPOINT}?${params.toString()}`;
		console.log("📥 Fetching mock survey files:", url);
		const response = await authenticatedFetch(url, {
			method: "GET",
			headers: { Accept: "application/json" },
		});

		if (!response.ok) {
			const text = await response.text().catch(() => "");
			console.error("❌ Fetch mock survey files failed:", response.status, text);
			throw new Error(`Fetch failed: ${response.status} ${response.statusText}`);
		}

		const data = await response.json();
		return data as any;
	},

	async removeFile(
		fileId: string | number
	  ): Promise<{ success: boolean; message?: string; deleted?: boolean; fileId?: number }> {
		const params = new URLSearchParams();
		params.set("fileid", String(fileId));
		const url = `${BASE_URL}${REMOVE_ENDPOINT}?${params.toString()}`;
		console.log("🗑️ Removing mock survey file:", url);

		const response = await authenticatedFetch(url, {
		  method: "DELETE",
		  headers: { Accept: "application/json" },
		});
	  
		if (!response.ok) {
		  const text = await response.text().catch(() => "");
		  console.error("❌ Remove file failed:", response.status, text);
		  throw new Error(`Remove failed: ${response.status} ${response.statusText}`);
		}
	  
		const data = await response.json().catch(() => null as any);
	  
		// 🔑 Normalize here
		const success = data?.status === "success" || data?.deleted === true;
	  
		return {
		  success,
		  message: data?.message,
		  deleted: data?.deleted,
		  fileId: data?.file_id,
		};
	  },

	async addComment(
		fileId: number | string,
		commentData: {
			file_id: number | string;
			file_name?: string; // File name for the payload
			paragraphIndex: number;
			paragraphText: string; // Full paragraph text for server-side validation
			startOffset: number;
			endOffset: number;
			selectedText: string;
			commentText: string;
			author: string;
			initials: string;
		}
	): Promise<{ success: boolean; message?: string; comment_id?: number }> {
		const url = `${BASE_URL}${ADD_COMMENT_ENDPOINT}`;
		console.log("💬 Adding comment:", url, commentData);

		const response = await authenticatedFetch(url, {
			method: "POST",
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
			},
			body: JSON.stringify(commentData),
		});

		if (!response.ok) {
			const text = await response.text().catch(() => "");
			console.error("❌ Add comment failed:", response.status, text);
			throw new Error(`Add comment failed: ${response.status} ${response.statusText} ${text}`);
		}

		const data = await response.json().catch(() => null as any);
		console.log("✅ Comment added successfully:", data);

		return {
			success: true,
			message: data?.message,
			comment_id: data?.comment_id,
		};
	},

	async downloadFileWithComments(
		fileId: number | string,
		fileName?: string
	): Promise<Blob> {
		const params = new URLSearchParams();
		params.set("fileid", fileId.toString());

		// Use the endpoint /api/v1/mock-survey/mock-survey-file-get with fileid to get specific file
		const url = `${BASE_URL}/api/v1/mock-survey/mock-survey-file-get?${params.toString()}`;
		console.log("📥 Downloading file with comments from:", url);

		const response = await authenticatedFetch(url, {
			method: "GET",
			headers: { Accept: "application/json" },
		});

		if (!response.ok) {
			const text = await response.text().catch(() => "");
			console.error("❌ Download failed:", response.status, text);
			throw new Error(`Download failed: ${response.status} ${response.statusText}`);
		}

		// The API returns JSON with filecontent as base64
		const data = await response.json();
		console.log("📥 API response:", data);

		// Check if we got a single file or array
		let fileData: any;
		if (Array.isArray(data.items) && data.items.length > 0) {
			fileData = data.items[0];
		} else if (data.fileblob || data.filecontent) {
			fileData = data;
		} else {
			throw new Error("No file content found in response");
		}

		// Decode base64 content to blob
		// API returns 'fileblob' instead of 'filecontent'
		const fileContentBase64 = fileData.fileblob || fileData.filecontent;
		if (fileContentBase64) {
			const binaryString = atob(fileContentBase64);
			const bytes = new Uint8Array(binaryString.length);
			for (let i = 0; i < binaryString.length; i++) {
				bytes[i] = binaryString.charCodeAt(i);
			}
			
			// Determine MIME type based on file extension
			const filename = fileData.filename || fileName || 'document.docx';
			const fileExtension = filename.split('.').pop()?.toLowerCase() || 'docx';
			let mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
			if (fileExtension === 'doc') {
				mimeType = 'application/msword';
			}
			
			const blob = new Blob([bytes], { type: mimeType });
			console.log("✅ File downloaded successfully:", blob.size, "bytes");
			return blob;
		} else {
			throw new Error("File content (fileblob/filecontent) not found in API response");
		}
	},

	async getComments(
		fileId: number | string
	): Promise<{
		count: number;
		items: Array<{
			id: number;
			fileid: number;
			userid: number;
			commentsjson: {
				author: string;
				fileId: number;
				fileName: string;
				initials: string;
				commentId: number | null;
				endOffset: number;
				commentText: string;
				startOffset: number;
				selectedText: string;
				paragraphText: string;
				paragraphIndex: number;
			};
			created_at: string;
			updated_at: string;
		}>;
	}> {
		const params = new URLSearchParams();
		params.set("fileid", String(fileId));
		const url = `${BASE_URL}${GET_COMMENTS_ENDPOINT}?${params.toString()}`;
		console.log("📥 Fetching comments:", url);

		const response = await authenticatedFetch(url, {
			method: "GET",
			headers: { Accept: "application/json" },
		});

		if (!response.ok) {
			const text = await response.text().catch(() => "");
			console.error("❌ Get comments failed:", response.status, text);
			throw new Error(`Get comments failed: ${response.status} ${response.statusText} ${text}`);
		}

		const data = await response.json();
		console.log("✅ Comments fetched successfully:", data);
		return data;
	},

	async removeComment(
		commentData: RemoveCommentRequest
	): Promise<RemoveCommentResponse> {
		const url = `${BASE_URL}${REMOVE_COMMENT_ENDPOINT}`;
		console.log("🗑️ Removing comment:", url, commentData);
		// Note: This API endpoint may not be implemented on the backend yet

		const response = await authenticatedFetch(url, {
			method: "POST",
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
			},
			body: JSON.stringify(commentData),
		});

		if (!response.ok) {
			const text = await response.text().catch(() => "");
			console.error("❌ Remove comment failed:", response.status, text);
			throw new Error(`Remove comment failed: ${response.status} ${response.statusText} ${text}`);
		}

		const data = await response.json().catch(() => null as any);
		console.log("✅ Comment removed successfully:", data);

		return {
			success: data?.status === "success" || data?.success === true || data?.deleted === true,
			message: data?.message,
			deleted: data?.deleted,
			comment_id: data?.comment_id || data?.commentId,
		};
	},

	async updateComment(
		commentData: UpdateCommentRequest
	): Promise<UpdateCommentResponse> {
		const url = `${BASE_URL}${UPDATE_COMMENT_ENDPOINT}`;
		console.log("📝 Updating comment:", url, commentData);

		const response = await authenticatedFetch(url, {
			method: "PUT",
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
			},
			body: JSON.stringify(commentData),
		});

		if (!response.ok) {
			const text = await response.text().catch(() => "");
			console.error("❌ Update comment failed:", response.status, text);
			throw new Error(`Update comment failed: ${response.status} ${response.statusText} ${text}`);
		}

		const data = await response.json().catch(() => null as any);
		console.log("✅ Comment updated successfully:", data);

		return data as UpdateCommentResponse;
	}
};


